import React, { useState } from 'react';
import { Calendar, Loader, Activity } from 'lucide-react';
import { ServiceRow } from './ServiceRow';
import { GoogleAccountInfo, GoogleCalendar } from './useGoogleAccount';

interface CalendarRowProps {
  active: boolean;
  connected: boolean;
  expanded: boolean;
  accountInfo: GoogleAccountInfo;
  googleCalendars: GoogleCalendar[];
  fetchingCalendars: boolean;
  onToggle: () => void;
  onActivate: () => void;
  onSave: (updates: { sync_activities?: boolean; sync_activities_calendar_id?: string; google_calendar_ids?: string[] }) => Promise<boolean>;
}

export function CalendarRow({
  active,
  connected,
  expanded,
  accountInfo,
  googleCalendars,
  fetchingCalendars,
  onToggle,
  onActivate,
  onSave,
}: CalendarRowProps) {
  const [saving, setSaving] = useState(false);
  const selectedCalendars = accountInfo.google_calendar_ids;

  const handleToggleCalendar = async (calendarId: string) => {
    const next = selectedCalendars.includes(calendarId)
      ? selectedCalendars.filter(id => id !== calendarId)
      : [...selectedCalendars, calendarId];

    setSaving(true);
    await onSave({ google_calendar_ids: next });
    setSaving(false);
  };

  const handleToggleSyncActivities = async (value: boolean) => {
    setSaving(true);
    await onSave({ sync_activities: value });
    setSaving(false);
  };

  const handleSelectActivityCalendar = async (calendarId: string) => {
    setSaving(true);
    await onSave({ sync_activities_calendar_id: calendarId });
    setSaving(false);
  };

  return (
    <ServiceRow
      icon={Calendar}
      title="Calendar"
      description="Sincronizza eventi e attività con Google Calendar"
      active={active}
      connected={connected}
      expanded={expanded}
      saving={saving}
      onToggle={onToggle}
      onActivate={onActivate}
    >
      <div className="space-y-4 py-2">
        {/* Lista calendari */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-[#37352F] dark:text-white">Calendari sincronizzati</p>
            {fetchingCalendars && <Loader className="h-3 w-3 animate-spin text-[#787774]" />}
          </div>
          {googleCalendars.length === 0 && !fetchingCalendars ? (
            <p className="text-xs text-[#787774] dark:text-white/40">Nessun calendario disponibile</p>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {googleCalendars.map(cal => (
                <label
                  key={cal.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[rgba(55,53,47,0.03)] dark:hover:bg-white/[0.03] cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedCalendars.includes(cal.id)}
                    onChange={() => handleToggleCalendar(cal.id)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-[#2383E2] focus:ring-[#2383E2]"
                  />
                  <span className="text-xs text-[#37352F] dark:text-white truncate">{cal.summary}</span>
                  {cal.primary && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-[#2383E2]/10 text-[#2383E2] rounded uppercase font-medium">
                      Primary
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Sync attività */}
        <div className="pt-3 border-t border-[rgba(55,53,47,0.06)] dark:border-white/[0.06]">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Activity className="h-3.5 w-3.5 text-[#787774]" />
              <span className="text-xs font-medium text-[#37352F] dark:text-white">Sincronizza attività su Calendar</span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={accountInfo.sync_activities}
                onChange={(e) => handleToggleSyncActivities(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:bg-[#2383E2] peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all" />
            </label>
          </div>
          {accountInfo.sync_activities && (
            <div className="mt-2">
              <label className="block text-xs text-[#787774] dark:text-white/50 mb-1">Calendario destinazione attività</label>
              <select
                value={accountInfo.sync_activities_calendar_id || ''}
                onChange={(e) => handleSelectActivityCalendar(e.target.value)}
                className="w-full text-xs px-2 py-1.5 rounded-md border border-[rgba(55,53,47,0.12)] dark:border-white/10 bg-white dark:bg-[#191919] focus:outline-none focus:border-[#2383E2]"
              >
                <option value="">— Seleziona —</option>
                {googleCalendars.map(cal => (
                  <option key={cal.id} value={cal.id}>{cal.summary}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
    </ServiceRow>
  );
}
